import { useCallback, useEffect, useState } from "react";
import { Nullable, TriggeredHook } from "sonobello.utilities.react";

import { useObx } from "../../../utils/UseObx";
import ApiRequestPaths from "../../Constants/ApiRequestPaths";
import { CalendarFailure } from "../Components/CalendarManager";
import { ICalendarHookProps } from "../Components/CenterCalendarWorker";
import Calendar from "../Types/Calendar";
import CalendarFailureStatusCodeMap from "../Types/CalendarFailureStatusCodeMap";
import ICalendar from "../Types/ICalendar";
import { ICalendarResponse } from "../Types/ICalendarResponse";

export interface ICenterCalendarHookProps {
  /** The id of the session's contact. */
  contactId: string;
  /** The id of the session's patient. */
  patientId: string;
}

/** Hook which obtains the OPS calendar for the given center id. */
export const useGetCenterCalendar: TriggeredHook<
  ICalendar,
  undefined,
  CalendarFailure,
  ICenterCalendarHookProps & ICalendarHookProps
> = ({ centerId, centerTimeZone, contactId, patientId }) => {
  const [calendar, setCalendar] = useState<Nullable<ICalendar>>(null);
  const { res, err, loading, setReq } = useObx<ICalendarResponse>("Get Calendar", {
    config: { params: { numberOfDays: process.env.REACT_APP_CALENDAR_MAXIMUM_DAYS } },
    preventOnUnauthorizedRecovery: true
  });
  useEffect(() => setCalendar(res?.payload ? new Calendar(res.payload, centerTimeZone) : null), [res]);
  const execute = useCallback(
    () => setReq(r => r && { ...r, url: ApiRequestPaths.getCenterCalendar(contactId, patientId, centerId) }),
    []
  );
  return {
    result: calendar,
    error: err?.error.response
      ? CalendarFailureStatusCodeMap[err.error.response.status] || CalendarFailure.NotFound
      : null,
    isLoading: loading,
    execute
  };
};
